import { useEffect, useMemo, useState } from 'react';
import { portfolioApi } from '@/services/portfolio-api';
import type { IIssueInProgressDailyTrendRow } from '@skystone/shared';
import type { IMetricViewFilters } from '@/hooks/use-metric-view-filters';
import { TrendCard } from './TrendCard';
import { bucketDailyWipToWeekly } from '@/lib/metric-view/weekly-bucketing';
import { adaptWipTrendPointsToLegacyRows } from '@/lib/wip-trend-adapter';
import { buildBucketAxisProps, todayIsoUtc } from '@/lib/metric-view/bucket-format';

interface IWipTrendCardProps {
  filters: IMetricViewFilters;
}

export function WipTrendCard({ filters }: Readonly<IWipTrendCardProps>) {
  // PRODOPSAI-418: migrated off legacy `issueInProgressDailyApi.getTrend`
  // onto the canonical `portfolioApi.getWipTrend`. Field names differ
  // (`date`/`count` vs `snapshotDate`/`distinctCount`); the adapter keeps
  // the existing `bucketDailyWipToWeekly` helper untouched.
  const [rows, setRows] = useState<IIssueInProgressDailyTrendRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    portfolioApi.getWipTrend({
      from: filters.from,
      to: filters.to,
      productId: filters.productId || undefined,
      teamId: filters.teamId || undefined,
    }).then((res) => {
      if (controller.signal.aborted) return;
      setRows(adaptWipTrendPointsToLegacyRows(res));
      setLoading(false);
    }).catch((err) => {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load WIP trend.');
      setLoading(false);
    });

    return () => controller.abort();
  }, [filters.from, filters.to, filters.productId, filters.teamId]);

  const trendData = useMemo(() => (rows ? bucketDailyWipToWeekly(rows) : []), [rows]);
  const activeType = filters.issueType === 'epic' ? 'all' : filters.issueType;
  const axis = useMemo(
    () => buildBucketAxisProps({
      today: todayIsoUtc(),
      rangeFrom: filters.from,
      rangeTo: filters.to,
    }),
    [filters.from, filters.to],
  );

  return (
    <TrendCard
      title="Work In Progress"
      description="Items in 'In Progress' over time, by issue type."
      data={trendData}
      activeType={activeType}
      loading={loading}
      error={error}
      axis={axis}
    />
  );
}
